/*
Thin analytics layer for the marketing/product funnel.

Funnel events go to Amplitude (initialized in index.js with autocapture) and, when
a GA4 measurement id is configured, to Google Analytics 4 as well. GA4 is loaded
lazily in index.js only if REACT_APP_GA4_ID is set — so until then `window.gtag`
is absent and the GA4 calls here are silent no-ops.

Every call is best-effort and swallows its own errors: analytics must never throw
into product code or break a user flow.
*/
import * as amplitude from "@amplitude/analytics-browser";
import { getAttributionProps } from "utils/attribution.js";

// Canonical event names. Keep these stable — dashboards and funnels key off them.
export const EVENTS = {
  EXAMPLE_OPENED: "example_opened",
  // The user got their OWN data onto the map. `method` says how: paste | upload |
  // map_click | search. This is the middle of the visit→solve funnel, which was
  // invisible — we could see 48 people open an optimizer and 2 solve, and nothing
  // about what happened in between. Fire it where rows enter, not in the reducer,
  // so `method` is the thing the person actually did.
  DATA_ENTERED: "data_entered",
  SOLVE_STARTED: "solve_started", // enqueued a run — the activation signal
  SOLVE_SUBMITTED: "solve_submitted", // backend accepted the queued run
  SOLVE_REQUEUED: "solve_requeued", // backend at capacity (429) — will retry
  SOLVE_FAILED: "solve_failed",
  SIGNED_IN: "signed_in",
  SIGNED_UP: "sign_up", // GA4's standard name — imported into Google Ads as the conversion
  RUNS_CLAIMED: "runs_claimed", // anon runs re-owned to the account on sign-in
  CTA_CLICKED: "cta_clicked",
  LEAD_CAPTURED: "generate_lead", // GA4's standard lead-conversion name
  PLAN_UPGRADED: "plan_upgraded", // free→paid transition — revenue-conversion proxy
  // A backend call failed, carrying the ROUTE that failed. Every backend error
  // already renders through one friendly sentence — "You need to be signed in to
  // do that." — which is right for the person and useless for us: on 2026-08-28 a
  // signed-out visitor could not run an example and nothing recorded which of the
  // ~20 endpoints returned the 401, so diagnosing it meant asking them to
  // reproduce it with the network tab open. The route was on the error object the
  // whole time; it just never left the browser.
  REQUEST_FAILED: "request_failed",
  // Run was pressed with some travel-cost pairs empty, and the person chose what
  // to do: `choice` is calculate | solve_anyway | cancel, with `missingPairs` and
  // `totalPairs`. Tells us whether that prompt helps or is just in the way.
  PARTIAL_TRAVEL_COSTS_CHOICE: "partial_travel_costs_choice",
  // The Run optimization button was pressed. Sent once per press, when its outcome
  // is known (so after any travel-cost prompt), and separate from SOLVE_STARTED so
  // a press that didn't lead to a run still shows up. Properties:
  //   `mode` example | own, from activeMode exactly as SOLVE_STARTED;
  //   `hasExampleSlug` the scenario came from an example (true even after "Edit
  //     scenario directly", when `mode` is own);
  //   `firstRun` nothing run yet here (this example, or anything in this browser);
  //   `emphasized` the button was standing out; `modelType`;
  //   `stopped` null only when the run was queued. Otherwise why not: invalid |
  //     multi_day | queue_full | amendments_declined | gap_dialog_cancelled |
  //     travel_costs_failed | could_not_start.
  // Tells us whether emphasising the button moves example-to-solve.
  RUN_CLICKED: "run_clicked",
  /*
  The Model Builder wizard, step by step (components/domain/wizard/wizardTracking.js
  has the full dictionary). 132 visitors opened it in 30 days, 5 reached a model
  page and none solved, and nothing said which step lost them. One "wizard visit"
  runs from the wizard mounting to it completing, being skipped, or being left.
    wizard_started        { entry, cta_id }
    wizard_step_viewed    { step_key, step_position, visible_step_count, model }
    wizard_step_completed { step_key, step_position, seconds_on_step, model, choice, ... }
    wizard_step_back      { step_key, step_position, seconds_on_step, model }
    wizard_skipped        { step_key, step_position, seconds_in_wizard, model }
    wizard_completed      { model, seconds_in_wizard }
    wizard_abandoned      { step_key, step_position, seconds_in_wizard, model, via }
  */
  WIZARD_STARTED: "wizard_started",
  WIZARD_STEP_VIEWED: "wizard_step_viewed",
  WIZARD_STEP_COMPLETED: "wizard_step_completed",
  WIZARD_STEP_BACK: "wizard_step_back",
  WIZARD_SKIPPED: "wizard_skipped",
  WIZARD_COMPLETED: "wizard_completed",
  WIZARD_ABANDONED: "wizard_abandoned",
};

/*
The path this page load started on, read once when the app boots. Lets a page
tell "arrived here directly" from "navigated here inside the app" without
listening to every route change.
*/
export const BOOT_PATHNAME = typeof window !== "undefined" ? String(window.location.pathname || "") : "";

/*
The last CTA_CLICKED, remembered in memory for a few seconds.

A CTA fires CTA_CLICKED and then navigates, so the page it opens can say which
CTA brought the visitor (the wizard's `cta_id`) without every link having to pass
it along. Recorded before the analytics-disabled check: it is a plain in-memory
note that never leaves the tab, and it keeps the behaviour testable here.
*/
let lastCtaClick = null;

/**
 * The CTA clicked within the last `maxAgeMs`, once: reading it clears it, so one
 * click is never credited to two pages.
 *
 * @param {number} [maxAgeMs=10000]
 * @returns {{ cta_id: string, page: string }|null}
 */
export function takeRecentCtaClick(maxAgeMs = 10000) {
  const click = lastCtaClick;
  lastCtaClick = null;
  if (!click || Date.now() - click.at > maxAgeMs) return null;
  return { cta_id: click.cta_id, page: click.page };
}

/*
Whether analytics is live in this browser.

Automated and local-dev traffic must not reach analytics, so index.js skips
amplitude.init entirely for them. But the SDK QUEUES calls made before (or
without) init, in memory, with no cap and no drain — so on `npm start`, or under
Playwright, every track() ever called was retained for the life of the tab.
recordRequestFailure fires one per failed backend call and the solve poller runs
every 750ms–5s, so a long local session against a flaky backend accumulated
events forever. The old comment in index.js noted the queueing behaviour without
noticing it was a leak.

The predicate lives here, next to the calls it gates, and index.js imports it so
the boot path and the event layer cannot disagree about who is being measured.
*/
/*
  A permanent, per-device opt-out for our own visits.

  We browse the live site from phones and laptops like anyone else, and those
  sessions are indistinguishable from a customer's: the hostname is production,
  there is no webdriver flag, and we do not sign in — checked against Amplitude,
  neither company email resolves to a user, so every internal visit is anonymous
  and device-scoped. Nothing about the request identifies it.

  So it has to be declared once per device. Visit any page with `?analytics=off`
  and this stores a flag; `?analytics=on` clears it. It survives navigation and
  restarts because it lives in localStorage rather than the URL.

  Wrapped in try/catch: Safari private mode and blocked-storage browsers throw on
  access, and analytics must never be the reason a page fails to boot.
*/
const OPT_OUT_KEY = "optimiciti.analyticsOptOut";

/**
 * Read (and update) this device's opt-out.
 *
 * `search` is a parameter rather than being read from `window` so it can be
 * exercised directly: CRA's Jest predates `@jest-environment-options`, so a test
 * cannot pin the page URL, and a version of this that only read globals could
 * not be tested at all on the paths that matter.
 *
 * @param {string} [search] - query string to interpret; defaults to the page's.
 * @returns {boolean} whether this device has opted out.
 */
export function readAnalyticsOptOut(search) {
  if (typeof window === "undefined") return false;
  try {
    const query = search === undefined ? window.location.search : search;
    const flag = new URLSearchParams(query).get("analytics");
    if (flag === "off") window.localStorage.setItem(OPT_OUT_KEY, "1");
    if (flag === "on") window.localStorage.removeItem(OPT_OUT_KEY);
    return window.localStorage.getItem(OPT_OUT_KEY) === "1";
  } catch (e) {
    return false;
  }
}

/*
  Hosts that are never a real visitor. `*.amplifyapp.com` is the Amplify preview
  domain the `develop` branch deploys to — 32 "users" in the last 30 days, all of
  them us checking a build before promotion. It was missed when localhost was
  added because the preview host looks like production from inside the app.
*/
const NON_PRODUCTION_HOST = /^(localhost|127\.0\.0\.1)$|\.amplifyapp\.com$/;

/** Exported for the same reason as above: the page's hostname is not settable
 *  under this Jest version, so the rule is tested directly. */
export const isNonProductionHost = (hostname) =>
  NON_PRODUCTION_HOST.test(String(hostname || ""));

/*
  Automated clients that run JavaScript.

  Most crawlers never execute JS and so never reach Amplitude at all. The ones
  that DO are the problem: headless Chrome, SEO auditors, uptime monitors, link
  unfurlers and the newer LLM crawlers all boot the app and register as people.
  They are a large part of why the denominator looks the way it does — 392 of
  459 "external users" in the 30 days to 4 Sep arrived with no referrer and no
  campaign, viewed 1.28 pages, and left.

  Two deliberate limits, so nobody reads more into this than it does:

  1. It cannot catch a crawler that spoofs a browser user agent, and the ones
     worth worrying about increasingly do. This reduces noise; it does not
     produce a clean number.
  2. The token list is SPECIFIC rather than a generic /bot/ match, because a
     false positive silently deletes a real visitor from the data, which is
     worse than keeping some noise. A generic /bot/i would drop every Android
     user on a CUBOT handset — the string appears in their user agent.
*/
const CRAWLER_UA =
  /(googlebot|bingbot|yandexbot|duckduckbot|baiduspider|applebot|slurp|ahrefsbot|semrushbot|mj12bot|dotbot|petalbot|bytespider|amazonbot|dataforseo|screaming frog|sitebulb|crawler|spider|scrapy|python-requests|curl\/|wget\/|go-http-client|okhttp|java\/|headlesschrome|phantomjs|puppeteer|playwright|selenium|lighthouse|pagespeed|gtmetrix|pingdom|statuscake|uptimerobot|facebookexternalhit|twitterbot|linkedinbot|slackbot|discordbot|telegrambot|whatsapp|embedly|redditbot|pinterestbot|gptbot|oai-searchbot|chatgpt-user|claudebot|claude-web|anthropic-ai|perplexitybot|ccbot|google-extended)/i;

/** Exported so the token list is testable directly — navigator.userAgent is not
 *  reliably settable under this Jest version. */
export const isCrawlerUserAgent = (userAgent) => CRAWLER_UA.test(String(userAgent || ""));

const IS_CRAWLER =
  typeof navigator !== "undefined" && isCrawlerUserAgent(navigator.userAgent);

/*
  The opt-out is evaluated FIRST, and deliberately not last.

  It is the only one of these three with a side effect: reaching it is what
  records the declaration in localStorage. Behind the `||` it was unreachable
  whenever another rule already matched, so opting out from localhost or the
  preview build stored nothing, and the device was measured again as soon as it
  hit production — which is the one place the setting is meant to work.
*/
const OPTED_OUT = readAnalyticsOptOut();

export const ANALYTICS_DISABLED =
  OPTED_OUT ||
  IS_CRAWLER ||
  (typeof navigator !== "undefined" && navigator.webdriver === true) ||
  (typeof window !== "undefined" &&
    isNonProductionHost(window.location.hostname));

function gtag(...args) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag(...args);
  }
}

/** Record a funnel event. `props` is a flat object of primitive values. */
export function track(event, props = {}) {
  if (event === EVENTS.CTA_CLICKED && props && typeof props.cta_id === "string") {
    lastCtaClick = { cta_id: props.cta_id, page: typeof props.page === "string" ? props.page : null, at: Date.now() };
  }
  // No-op when analytics is off: the SDK would queue this in memory forever.
  if (ANALYTICS_DISABLED) return;
  // Every event carries the visitor's ad attribution (ft_*/lt_*) so funnels
  // can slice by campaign; explicit props win on collision.
  let merged = props;
  try {
    merged = { ...getAttributionProps(), ...props };
  } catch (e) {
    /* fall back to the caller's props */
  }
  try {
    amplitude.track(event, merged);
  } catch (e) {
    /* never break the app for analytics */
  }
  try {
    gtag("event", event, merged);
  } catch (e) {
    /* no-op */
  }
}

/*
Sending while the page goes away.

Amplitude batches events and sends them over fetch about once a second; a page
that is closing never gets there. The browser SDK's documented answer is to
switch to the beacon transport (navigator.sendBeacon, which the browser completes
after the page is gone) and flush. Switched back when the page is shown again
(a bfcache restore, or a tab coming back into view), because beacon sends are
not retried.
*/

/** track() for a page that is being left: queue, then flush over beacon. */
export function trackOnPageExit(event, props = {}) {
  if (ANALYTICS_DISABLED) return;
  try {
    amplitude.setTransport("beacon");
  } catch (e) {
    /* no-op */
  }
  track(event, props);
  try {
    amplitude.flush();
  } catch (e) {
    /* no-op */
  }
}

/**
 * Deliver anything already queued, over beacon, because the page may be going away.
 *
 * Trade-off: beacon sends are not retried, and a refused beacon (over the browser's
 * unload quota) drops those events instead of leaving them queued for the next
 * visit. Accepted for the wizard; see wizardTracking.js.
 */
export function flushOnPageExit() {
  if (ANALYTICS_DISABLED) return;
  try {
    amplitude.setTransport("beacon");
    amplitude.flush();
  } catch (e) {
    /* no-op */
  }
}

/** The page is back (bfcache restore or tab visible again): normal sends. */
export function restoreTransport() {
  if (ANALYTICS_DISABLED) return;
  try {
    amplitude.setTransport("fetch");
  } catch (e) {
    /* no-op */
  }
}

/**
 * track(), but resolving to whether Amplitude actually accepted the event.
 *
 * Exists for once-ever conversions guarded by a write-once flag. `track()` above
 * swallows every error by design, so a caller that sets its guard and then calls
 * it cannot tell a delivered event from a blocked one — and the guard then
 * claims the event already fired, which suppresses it permanently. That is not
 * hypothetical: sign_up was set up that way and lost one of three real sign-ups,
 * on the event Google Ads imports as the primary conversion.
 *
 * Callers await this and persist their guard only on `true`, so a blocked or
 * interrupted send simply retries on the person's next visit.
 *
 * Resolves false rather than rejecting — a failure to measure must never break
 * a user flow. The verdict speaks only for Amplitude: GA4 acknowledges nothing,
 * so it stays fire-and-forget here exactly as in track().
 *
 * @param {string} event
 * @param {object} props
 * @returns {Promise<boolean>} true only when Amplitude reported success
 */
export function trackAcknowledged(event, props = {}) {
  // No-op when analytics is off: the SDK would queue this in memory forever.
  if (ANALYTICS_DISABLED) return Promise.resolve(false);
  let merged = props;
  try {
    merged = { ...getAttributionProps(), ...props };
  } catch (e) {
    /* fall back to the caller's props */
  }
  try {
    gtag("event", event, merged);
  } catch (e) {
    /* no-op */
  }
  try {
    const result = amplitude.track(event, merged);
    const promise = result && result.promise;
    if (!promise || typeof promise.then !== "function") {
      // No SDK acknowledgement available (older SDK, or a stub in tests).
      // Report failure rather than success: an unconfirmed send must not be
      // allowed to set a permanent guard.
      return Promise.resolve(false);
    }
    return promise.then(
      (r) => Boolean(r) && r.code >= 200 && r.code < 300,
      () => false
    );
  } catch (e) {
    return Promise.resolve(false);
  }
}

/**
 * Mirror the captured ad attribution (the ft_ / lt_ props) onto the Amplitude user
 * profile as user properties, so PEOPLE — not just events — segment by ad
 * source. Deterministic replacement for the SDK's built-in attribution
 * tracking, whose captured properties proved unverifiable in practice. Safe to
 * call on every boot: the values only change when a new campaign visit lands.
 */
export function syncAttributionUserProps() {
  // No-op when analytics is off: the SDK would queue this in memory forever.
  if (ANALYTICS_DISABLED) return;
  try {
    const props = getAttributionProps();
    const keys = Object.keys(props);
    if (!keys.length) return;
    const identify = new amplitude.Identify();
    keys.forEach((k) => identify.set(k, props[k]));
    amplitude.identify(identify);
  } catch (e) {
    /* no-op */
  }
}

/** Attach a stable user id (and optional traits) so events attribute to a person. */
export function identifyUser(userId, traits = {}) {
  // No-op when analytics is off: the SDK would queue this in memory forever.
  if (ANALYTICS_DISABLED) return;
  try {
    if (userId) amplitude.setUserId(userId);
    const keys = traits ? Object.keys(traits) : [];
    if (keys.length) {
      const identify = new amplitude.Identify();
      keys.forEach((k) => {
        if (traits[k] != null) identify.set(k, traits[k]);
      });
      amplitude.identify(identify);
    }
  } catch (e) {
    /* no-op */
  }
  try {
    if (userId) gtag("set", { user_id: userId });
    if (traits && Object.keys(traits).length) gtag("set", "user_properties", traits);
  } catch (e) {
    /* no-op */
  }
}

/** Clear identity on sign-out so a shared browser doesn't cross-attribute. */
export function resetUser() {
  // No-op when analytics is off: the SDK would queue this in memory forever.
  if (ANALYTICS_DISABLED) return;
  try {
    amplitude.reset();
  } catch (e) {
    /* no-op */
  }
}
